import Modal from 'components/Modal';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import useToggle from '@alltrails/shared/hooks/useToggle';
import Logo from '@alltrails/shared/denali/components/Logo';
import Typography from 'components/Typography';
import Button from '@alltrails/shared/denali/components/Button';
import badgeCheckSrc from 'components/icons/badge-check.svg';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { getPriceTotal } from 'utils/PlanUtils';
import { Fragment, Key, useEffect } from 'react';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import logWebProModalPurchaseButtonTapped from '@alltrails/analytics/events/logWebProModalPurchaseButtonTapped';
import logWebProModalShown from '@alltrails/analytics/events/logWebProModalShown';
import WebProModalPurchaseButtonCopyText from '@alltrails/analytics/enums/WebProModalPurchaseButtonCopyText';
import * as styles from './PlusUpsell.module.scss';

const coupon = { discountPercentage: 20 };

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1111%3A72151&t=ZAThVNB9DfbdVKzP-0
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1806%3A76912&t=ZAThVNB9DfbdVKzP-4
export default function PlusUpsell() {
  const [isOpen, toggle] = useToggle(true);
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    logWebProModalShown({
      purchase_screen_type_plan: PurchaseScreenTypePlan.Promo,
      trigger: CarouselDisplayTrigger.WebPostSignup
    });
  }, []);

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: __AT_DATA__.currencyCode
  });

  const features = [
    <FormattedMessage defaultMessage="Download offline maps" />,
    <FormattedMessage defaultMessage="Get alerts for wrong turns" />,
    <FormattedMessage defaultMessage="Find trails by distance from you" />,
    <FormattedMessage defaultMessage="And more..." />
  ];

  const { plan } = __AT_DATA__;
  const discountedPricePerYear = getPriceTotal(plan, coupon);
  const percent = coupon.discountPercentage / 100;

  return (
    <Modal
      background="cream"
      isOpen={isOpen}
      onRequestClose={toggle}
      size="lg"
      headerClassName={styles.header}
      iconButtonVariant="default"
      closeButtonStyle={styles.close}
    >
      <div>
        <div className={styles.content}>
          <div className={styles.title}>
            <Logo className={styles.logo} color="dark" size="sm" variant="plus-logomark" />
            <Typography component="div" variant="heading700">
              <FormattedMessage defaultMessage="Take {percent, number, ::percent} off" values={{ percent }} />
            </Typography>
            <Typography component="div" variant="text300">
              <FormattedMessage defaultMessage="Make the most of every minute outside with AllTrails+" />
            </Typography>
          </div>
          <div className={styles.features}>
            {features.map((feature, index) => (
              <Fragment key={index as Key}>
                {index < features.length - 1 ? <img alt="" src={badgeCheckSrc} /> : <div />}
                <Typography component="div" variant="text300">
                  {feature}
                </Typography>
              </Fragment>
            ))}
          </div>
          <Button
            className={styles.button}
            onClick={() => {
              logWebProModalPurchaseButtonTapped({
                copy_text: WebProModalPurchaseButtonCopyText.Save20Now,
                purchase_screen_type_plan: PurchaseScreenTypePlan.Promo,
                trigger: CarouselDisplayTrigger.WebPostSignup
              });
            }}
            linkInfo={{ href: wrapUrlSafe('/plus?k=savetoday', languageRegionCode) }}
            testId="save-now"
            text={<FormattedMessage defaultMessage="Save {percent, number, ::percent} now" values={{ percent }} />}
            variant="primary"
            width="full"
          />
          <Typography className={styles.subtext} component="div" variant="text100bold">
            <FormattedMessage
              defaultMessage="Just {discountedPricePerYear} for the first year (that's {monthlyCost} a month). Renews annually at {priceTotal}."
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear),
                monthlyCost: formatter.format(discountedPricePerYear / 12),
                priceTotal: formatter.format(Number(plan.priceTotal))
              }}
            />
          </Typography>
        </div>
      </div>
    </Modal>
  );
}
